import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import FocusLock from 'react-focus-lock'
import { toast } from 'sonner'
import {
  X,
  Sparkles,
  ShieldCheck,
  Zap,
  Globe,
  LayoutGrid,
  Volume2,
  Check,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useLocale, LocalePicker } from '@baba/i18n/react'
import { localeMeta } from '@baba/i18n'
import {
  broadcastSubscriptionStatus,
  isSubscriptionActive,
  readCachedSubscription,
  type SubscriptionStatus,
  redeemPromoCode,
  validatePromoCode,
  type UsageSnapshot,
} from '@/lib/baba-api'
import {
  babaApi,
  usePricingQuery,
  useSubscriptionStatusQuery,
  useUsageQuery,
  useCreateCheckoutSessionMutation,
  useSendMagicLinkMutation,
  useRestoreSubscriptionMutation,
  type CreateCheckoutResult,
} from '../store/babaApi'
import { useAppDispatch } from '../store/hooks'
import { clearAuthToken, isSignedIn, getAuthEmail } from '@/lib/baba-auth'
import { trackPaywallViewed, trackCheckoutStarted, trackCheckoutSessionCreated, trackMagicLinkRequested } from '@/lib/analytics'
import { phStartRecording } from '@/lib/posthog'
import { getStoredReferralCode } from '@/lib/referral'
import { openPaddleCheckout } from '@/lib/paddle'
import { useSignIn } from './SignInModal'
import { startGoogleSignIn } from '@/lib/googleSignIn'

export type PaywallReason =
  | 'upgrade'
  | 'monthly-chars-reached'
  | 'text-too-long'
  | 'slang-mode'
  | 'summary-mode'
  | 'pdf'
  | 'image'
  | 'document'
  | 'voice'
  | 'word-alternatives'
  | 'favorites'
  | 'history'
  | 'limit-reached'
  | 'pro-feature'
  // Pro+ reasons — mirror mobile's PRO_PLUS_REASONS / isProPlusReason. Opening the
  // paywall with one of these preselects the Pro+ tier (see PRO_PLUS_REASONS
  // below); when Pro+ isn't configured yet, these fall back to the Pro paywall
  // with the same copy.
  | 'text-tutor-pro-limit'
  | 'conversation'
  | 'situations'
  | 'voice-tutor'
  // Pro (not Pro+) gates that mirror mobile's feature-lock reasons.
  | 'text-tutor-limit'
  | 'reply-drafts'

interface PaywallContextValue {
  open: (reason?: PaywallReason, options?: PaywallOpenOptions) => void
  close: () => void
}

const PaywallContext = createContext<PaywallContextValue | null>(null)

type BillingPlan = 'monthly' | 'yearly'

interface PaywallOpenOptions {
  plan?: BillingPlan
}

type PaywallEventDetail =
  | PaywallReason
  | {
      reason?: PaywallReason
      plan?: BillingPlan
    }

export function usePaywall(): PaywallContextValue {
  const ctx = useContext(PaywallContext)
  if (!ctx) return { open: () => {}, close: () => {} }
  return ctx
}

function normalizeReason(r: PaywallReason): ResolvedReason {
  if (r === 'limit-reached') return 'monthly-chars-reached'
  if (r === 'pro-feature') return 'upgrade'
  return r
}

type ResolvedReason = Exclude<PaywallReason, 'limit-reached' | 'pro-feature'>

type SubscriptionTier = 'pro' | 'pro_plus'

/**
 * Reasons that are Pro+ on mobile (mirrors mobile's PRO_PLUS_REASONS /
 * isProPlusReason). Opening the paywall with one of these preselects the
 * Pro+ tier, but only when Pro+ is actually configured (pricing.proPlus) —
 * otherwise the paywall falls back to Pro with the same reason copy.
 */
const PRO_PLUS_REASONS: Set<PaywallReason> = new Set([
  'text-tutor-pro-limit',
  'conversation',
  'situations',
  'voice-tutor',
])

/** Feature icon order — paired with catalog strings at render time. */
const FEATURE_ICONS: { key: keyof import('@baba/i18n').StringCatalog['paywall']['features']; icon: LucideIcon }[] = [
  { key: 'chars',         icon: Zap },
  { key: 'slang',         icon: Sparkles },
  { key: 'media',         icon: Globe },
  { key: 'voice',         icon: Volume2 },
  { key: 'crossPlatform', icon: LayoutGrid },
]

/** Only this reason shows the "you're out of characters" usage banner. */
const USAGE_BANNER_REASONS: Set<ResolvedReason> = new Set(['monthly-chars-reached'])

/** Radio glyph for the plan table rows — filled when selected. */
function PlanRadio({ selected }: { selected: boolean }) {
  return selected ? (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className="shrink-0">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="var(--accent-deep)" strokeWidth="1.5" />
      <circle cx="8" cy="8" r="3.5" fill="var(--accent-deep)" />
    </svg>
  ) : (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className="shrink-0">
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="var(--muted-2)" strokeWidth="1.5" />
    </svg>
  )
}

/** Small phone + laptop glyph for the "works everywhere" note. */
function DevicesIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 22 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
      <rect x="1" y="1" width="12" height="8.5" rx="1" />
      <line x1="1" y1="11" x2="13" y2="11" />
      <rect x="16" y="3" width="5.5" height="10.5" rx="1.2" />
      <line x1="17.6" y1="11.5" x2="19.9" y2="11.5" />
    </svg>
  )
}

/** Pro / Pro+ segmented control — mirrors mobile's PaywallTierToggle. Only rendered when Pro+ pricing is configured. */
function PaywallTierToggle({
  tier,
  onChange,
  labels,
}: {
  tier: SubscriptionTier
  onChange: (tier: SubscriptionTier) => void
  labels: import('@baba/i18n').StringCatalog['paywall']['tierToggle']
}) {
  return (
    <div
      className="mb-3 flex rounded-md border border-app p-0.5"
      role="radiogroup"
      aria-label={`${labels.pro} / ${labels.proPlus}`}
    >
      <button
        type="button"
        role="radio"
        aria-checked={tier === 'pro'}
        onClick={() => onChange('pro')}
        className="flex-1 h-9 rounded-[6px] text-[13px] font-semibold transition-colors text-app"
        style={{ background: tier === 'pro' ? 'var(--card-2)' : 'transparent' }}
      >
        <span className={tier === 'pro' ? 'text-app' : 'text-muted'}>{labels.pro}</span>
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={tier === 'pro_plus'}
        onClick={() => onChange('pro_plus')}
        className="flex-1 h-9 rounded-[6px] text-[13px] font-semibold transition-colors flex items-center justify-center gap-1.5"
        style={{ background: tier === 'pro_plus' ? 'var(--card-2)' : 'transparent' }}
      >
        <span className={tier === 'pro_plus' ? 'text-app' : 'text-muted'}>{labels.proPlus}</span>
        <span
          className="font-mono uppercase tracking-wider rounded"
          style={{
            fontSize: 9,
            padding: '1px 5px',
            background: tier === 'pro_plus' ? 'var(--accent)' : 'var(--accent-soft)',
            color: tier === 'pro_plus' ? 'var(--cta-fg)' : 'var(--accent-deep)',
          }}
        >
          {labels.badge}
        </span>
      </button>
    </div>
  )
}

/** Compact Pro vs Pro+ comparison shown below the plan table when Pro+ is configured. */
function PaywallProPlusComparison({
  copy,
}: {
  copy: import('@baba/i18n').StringCatalog['paywall']['proPlusComparison']
}) {
  return (
    <div className="mb-5">
      <div className="mb-1.5 px-1 text-[10.5px] font-semibold uppercase tracking-wider text-subtle">
        {copy.label}
      </div>
      <div className="rounded-md border border-app overflow-hidden">
        <table className="w-full border-collapse text-[12px]">
          <thead>
            <tr style={{ background: 'var(--card-2)' }}>
              <th className="py-2 px-3 text-left" />
              <th
                className="py-2 px-3 text-center font-semibold text-subtle"
                style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}
              >
                {copy.proHeader}
              </th>
              <th
                className="py-2 px-3 text-center font-semibold"
                style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink)' }}
              >
                {copy.plusHeader}
              </th>
            </tr>
          </thead>
          <tbody>
            {copy.rows.map((row) => (
              <tr key={row.label} className="border-t border-app">
                <td className="py-2 px-3 text-app font-medium">{row.label}</td>
                <td className="py-2 px-3 text-center text-muted">{row.pro}</td>
                <td className="py-2 px-3 text-center font-semibold text-app">
                  {row.proPlus}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Replaces the reason-feature bullets / feature-icon grid when the Pro+ tier
 * is selected — mirrors mobile's PaywallProPlusBenefits (Pro baseline, then
 * the Pro+ differentiator).
 */
function PaywallProPlusBenefits({
  copy,
}: {
  copy: import('@baba/i18n').StringCatalog['paywall']['proPlusBenefits']
}) {
  return (
    <div className="mb-5 rounded-md border border-app overflow-hidden">
      <div className="p-3.5">
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className="font-mono uppercase tracking-wider rounded"
            style={{ fontSize: 9, padding: '1px 6px', background: 'var(--card-2)', color: 'var(--muted)' }}
          >
            {copy.proBadge}
          </span>
          <span className="text-[13px] font-semibold text-app">{copy.proHeader}</span>
        </div>
        <ul className="space-y-1.5">
          {copy.proItems.map((item) => (
            <li key={item} className="flex items-start gap-2 text-[12.5px] text-app leading-snug">
              <span className="shrink-0 mt-0.5" style={{ color: 'var(--muted-2)' }}>
                <Check className="h-3.5 w-3.5" strokeWidth={1.75} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="p-3.5 border-t border-app" style={{ background: 'var(--accent-soft)' }}>
        <div className="flex items-center gap-2 mb-2.5">
          <span
            className="font-mono uppercase tracking-wider rounded"
            style={{ fontSize: 9, padding: '1px 6px', background: 'var(--accent)', color: 'var(--cta-fg)' }}
          >
            {copy.plusBadge}
          </span>
          <span className="text-[13px] font-semibold text-app">{copy.plusHeader}</span>
        </div>
        <ul className="space-y-1.5">
          {copy.plusItems.map((item) => (
            <li key={item} className="flex items-start gap-2 text-[12.5px] text-app leading-snug">
              <span className="shrink-0 mt-0.5" style={{ color: 'var(--accent-deep)' }}>
                <Check className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function parseBillingPlan(raw: unknown): BillingPlan | null {
  return raw === 'monthly' || raw === 'yearly' ? raw : null
}

// Promo codes match BABA-XXXX-XXXX (alphanumeric segments). Client-side
// format check avoids burning the server's daily validate quota on obvious
// garbage; the server enforces the authoritative check either way.
const PROMO_CODE_RE = /^BABA-[A-Z0-9]{4}-[A-Z0-9]{4}$/i

// Same shape as the magic-link form's own validation below — reused (not
// re-invented) to decide when a typed-but-unfinished email is "complete
// enough" to echo back in copy, so the double-pay note doesn't churn on
// every keystroke ("use this email (a", "(a@", "(a@g"...).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function parsePaywallEventDetail(detail: unknown): { reason: PaywallReason; plan: BillingPlan | null } {
  if (typeof detail === 'object' && detail !== null) {
    const value = detail as { reason?: PaywallReason; plan?: BillingPlan }
    return {
      reason: 'reason' in value && value.reason ? value.reason : 'upgrade',
      plan: 'plan' in value ? parseBillingPlan(value.plan) : null,
    }
  }
  return {
    reason: (detail || 'upgrade') as PaywallReason,
    plan: null,
  }
}

function writeSessionStorage(key: string, value: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, value)
  } catch {}
}

function readSessionStorage(key: string): string | null {
  try {
    if (typeof sessionStorage !== 'undefined') return sessionStorage.getItem(key)
  } catch {}
  return null
}

function removeSessionStorage(key: string): void {
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(key)
  } catch {}
}

/**
 * sessionStorage key holding `{ plan }` across a sign-in that reloads this tab:
 * the Google OAuth round-trip, or an emailed link opened in another tab (a
 * sign-in anywhere reloads every open tab; see lib/accountLocalState.ts).
 */
const OAUTH_PAYWALL_KEY = 'baba.oauth.paywall'

// Reasons that mean "user explicitly asked for pricing" — always show the
// paywall regardless of sign-in state. Everything else is a feature-gate or
// usage-limit, where anonymous users benefit from signing up free first
// (the free tier may already cover their need).
const ALWAYS_PAYWALL_REASONS: Set<PaywallReason> = new Set(['upgrade'])

// Map a feature-gate reason to the SignInModal intent that explains the
// most relevant value prop on the sign-in screen. Defaults to 'upgrade-free'
// which leads with the free character allowance, appropriate for most cases.
function intentForReason(reason: PaywallReason): import('./SignInModal').SignInIntent {
  if (reason === 'favorites' || reason === 'history') return 'library'
  if (reason === 'monthly-chars-reached' || reason === 'limit-reached' || reason === 'text-too-long') {
    return 'upgrade-free'
  }
  return 'upgrade-free'
}

/**
 * True when this subscription already covers what the paywall would sell for
 * `reason`. The one exception is a Pro subscriber hitting a Pro+ reason: that
 * is a real upsell, but only when Pro+ is confirmed purchasable — otherwise
 * the modal would pitch the Pro tier they already own.
 */
function subscriptionCovers(
  status: SubscriptionStatus | null | undefined,
  reason: PaywallReason,
  proPlusAvailable: boolean,
): boolean {
  if (!isSubscriptionActive(status)) return false
  if (PRO_PLUS_REASONS.has(reason) && proPlusAvailable) return status?.tier === 'pro_plus'
  return true
}

/** A status we can act on: actually fetched, not a placeholder or a failed read. */
function isResolved(status: SubscriptionStatus | null | undefined): status is SubscriptionStatus {
  return !!status && status.status !== 'unknown' && !status.fetchFailed
}

const ALREADY_PRO_TOAST_ID = 'paywall-already-pro'

function toastAlreadyPro(): void {
  toast.success('You’re on baba Pro. Everything is unlocked.', { id: ALREADY_PRO_TOAST_ID })
}

export function PaywallProvider({ children }: { children: React.ReactNode }) {
  const dispatch = useAppDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [reason, setReason] = useState<PaywallReason>('upgrade')
  const [initialPlan, setInitialPlan] = useState<BillingPlan | null>(null)
  const { open: openSignIn } = useSignIn()

  const open = useCallback(
    (r: PaywallReason = 'upgrade', options?: PaywallOpenOptions) => {
      // Two-step funnel: anonymous users hitting a feature gate or usage
      // limit go to SignInModal first. They get the free tier; if that's
      // still not enough they'll see the paywall the next time they hit a
      // limit. Explicit "Upgrade" clicks bypass this and show pricing.
      if (!ALWAYS_PAYWALL_REASONS.has(r) && !isSignedIn()) {
        openSignIn(intentForReason(r))
        return
      }
      const show = () => {
        setReason(r)
        setInitialPlan(options?.plan ?? null)
        setIsOpen(true)
      }
      if (!isSignedIn()) {
        show()
        return
      }
      // A signed-in subscriber must never see the pitch, not even for the
      // moment it takes the status request to land. The pricing page's
      // ?plan= link and the Google sign-in return both open the paywall on
      // page load, before any component has fetched the plan, so the plan is
      // settled first: the stored status answers instantly, the query
      // confirms it, and an unresolved answer keeps the paywall shut.
      const cached = readCachedSubscription()
      if (isSubscriptionActive(cached) && !PRO_PLUS_REASONS.has(r)) {
        toastAlreadyPro()
        return
      }
      void (async () => {
        let fetched: SubscriptionStatus | null = null
        try {
          fetched = await dispatch(babaApi.endpoints.subscriptionStatus.initiate(undefined)).unwrap()
        } catch {}
        // A failed refresh falls back to what this browser last knew rather
        // than to "free" — the alternative asks a paying customer to pay again.
        const status = isResolved(fetched) ? fetched : isResolved(cached) ? cached : null
        if (!status) {
          toast.error('Could not check your plan just now. Try again in a moment.', { id: ALREADY_PRO_TOAST_ID })
          return
        }
        if (status.authExpired) {
          openSignIn(intentForReason(r))
          return
        }
        let proPlusAvailable = false
        if (isSubscriptionActive(status) && PRO_PLUS_REASONS.has(r)) {
          try {
            const pricing = await dispatch(babaApi.endpoints.pricing.initiate(undefined)).unwrap()
            proPlusAvailable = !!pricing?.proPlus
          } catch {}
        }
        if (subscriptionCovers(status, r, proPlusAvailable)) toastAlreadyPro()
        else show()
      })()
    },
    [dispatch, openSignIn],
  )

  const close = useCallback(() => {
    // Closed on purpose: a later reload must not bring the pitch back.
    removeSessionStorage(OAUTH_PAYWALL_KEY)
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = parsePaywallEventDetail((e as CustomEvent<PaywallEventDetail>).detail)
      open(detail.reason, detail.plan ? { plan: detail.plan } : undefined)
    }
    window.addEventListener('baba:show-paywall', handler)
    return () => window.removeEventListener('baba:show-paywall', handler)
  }, [open])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    const plan = parseBillingPlan(url.searchParams.get('plan'))
    if (!plan) return
    // ?tier=pro_plus preselects Pro+ (a Pro+ reason; no generic Pro+ upsell
    // exists, so 'voice-tutor' is the stand-in). Anything else is plain Pro.
    open(url.searchParams.get('tier') === 'pro_plus' ? 'voice-tutor' : 'upgrade', { plan })
    url.searchParams.delete('plan')
    url.searchParams.delete('tier')
    url.searchParams.delete('src')
    const clean = `${url.pathname}${url.search}${url.hash}`
    window.history.replaceState(null, '', clean)
  }, [open])

  // Reopen the paywall after a Google OAuth round-trip. The Google sign-in
  // button stashes the billing plan before redirecting away; the OAuth
  // handoff (AppShell) adopts the session and reloads, landing the user
  // back here signed-in. Without this they'd have to find Upgrade again.
  useEffect(() => {
    const stash = readSessionStorage(OAUTH_PAYWALL_KEY)
    if (!stash) return
    removeSessionStorage(OAUTH_PAYWALL_KEY)
    if (!isSignedIn()) return
    let plan: BillingPlan | null = null
    try {
      plan = parseBillingPlan((JSON.parse(stash) as { plan?: unknown })?.plan)
    } catch {}
    open('upgrade', plan ? { plan } : undefined)
  }, [open])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handler)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handler)
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen, close])

  return (
    <PaywallContext.Provider value={{ open, close }}>
      {children}
      <PaywallModal isOpen={isOpen} reason={reason} initialPlan={initialPlan} onClose={close} />
    </PaywallContext.Provider>
  )
}

function PaywallModal({
  isOpen,
  reason,
  initialPlan,
  onClose,
}: {
  isOpen: boolean
  reason: PaywallReason
  initialPlan: BillingPlan | null
  onClose: () => void
}) {
  const dispatch = useAppDispatch()
  const [billing, setBilling] = useState<BillingPlan>('yearly')
  const [tier, setTier] = useState<SubscriptionTier>('pro')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [magicSent, setMagicSent] = useState(false)
  const [showPromo, setShowPromo] = useState(false)
  const [promoCode, setPromoCode] = useState('')
  const [promoValidated, setPromoValidated] = useState<{ discount: number; expiresAt?: string | null } | null>(null)
  const [promoError, setPromoError] = useState<string | null>(null)
  const [promoValidating, setPromoValidating] = useState(false)
  const [signedIn, setSignedIn] = useState(false)

  const { locale, t } = useLocale()
  const { dir: layoutDir } = localeMeta(locale)

  const resolvedReason = useMemo(() => normalizeReason(reason), [reason])
  const reasonCopy = t.paywall.reasons[resolvedReason]
  const showUsageBanner = USAGE_BANNER_REASONS.has(resolvedReason)
  // 'upgrade' keeps the fixed icon grid (it's the general pitch); every other
  // reason gets its own bullet list so the copy actually explains what unlocks.
  const reasonFeatureList = resolvedReason === 'upgrade' ? null : t.paywall.reasonFeatures[resolvedReason]

  const { data: pricing } = usePricingQuery(undefined, { skip: !isOpen })
  const proPlusAvailable = !!pricing?.proPlus
  const { data: subscriptionStatus } = useSubscriptionStatusQuery(undefined, { skip: !isOpen || !signedIn })
  const skipUsage = !isOpen || !showUsageBanner || !signedIn
  const { data: usage } = useUsageQuery(undefined, { skip: skipUsage })
  const [createCheckout] = useCreateCheckoutSessionMutation()
  const [sendMagicLinkMutation] = useSendMagicLinkMutation()
  const [restoreSubscription] = useRestoreSubscriptionMutation()

  useEffect(() => {
    if (isOpen && initialPlan) setBilling(initialPlan)
  }, [isOpen, initialPlan])

  // Preselect Pro+ for the reasons mobile gates behind Pro+, but only once
  // pricing confirms Pro+ is actually configured — otherwise stay on Pro so
  // the plan table and CTA never advertise a tier that can't be purchased.
  useEffect(() => {
    if (!isOpen) return
    setTier(PRO_PLUS_REASONS.has(reason) && proPlusAvailable ? 'pro_plus' : 'pro')
  }, [isOpen, reason, proPlusAvailable])

  useEffect(() => {
    if (!isOpen) return
    const saved = getAuthEmail()
    if (saved) setEmail(saved)
    setSignedIn(isSignedIn())
    trackPaywallViewed({ reason })
    // Start replay on paywall view: the highest-leverage surface and the one
    // we most want to watch. Idempotent once the client is up.
    phStartRecording()
  }, [isOpen, reason])

  useEffect(() => {
    if (!isOpen || !magicSent || signedIn) return
    const started = Date.now()
    const interval = setInterval(() => {
      if (isSignedIn()) {
        clearInterval(interval)
        // Any status cached before this sign-in belongs to the signed-out
        // session; re-read it so an existing subscriber is recognised.
        dispatch(babaApi.util.invalidateTags([{ type: 'Subscription', id: 'status' }]))
        setSignedIn(true)
        setMagicSent(false)
        toast.success('Signed in.')
      } else if (Date.now() - started > 5 * 60 * 1000) {
        clearInterval(interval)
      }
    }, 3000)
    return () => clearInterval(interval)
  }, [isOpen, magicSent, signedIn, dispatch])

  // Signing in from inside the paywall can reveal an existing subscriber
  // (their session had lapsed). Close the pitch as soon as the status says so.
  useEffect(() => {
    if (!isOpen || !signedIn || !subscriptionStatus) return
    if (subscriptionCovers(subscriptionStatus, reason, proPlusAvailable)) {
      broadcastSubscriptionStatus(subscriptionStatus)
      toastAlreadyPro()
      onClose()
    }
  }, [isOpen, signedIn, subscriptionStatus, reason, proPlusAvailable, onClose])

  useEffect(() => {
    if (!isOpen || !signedIn) return
    if (subscriptionStatus?.authExpired) {
      clearAuthToken({ keepEmail: true })
      setSignedIn(false)
      setMagicSent(false)
      toast.info('Your sign-in expired. Sign in again to access your Pro account.')
    }
  }, [isOpen, signedIn, subscriptionStatus])

  const handlePromoValidate = async (code: string) => {
    setPromoValidating(true)
    setPromoError(null)
    try {
      const result = await validatePromoCode(code)
      if (result.valid) {
        setPromoValidated({ discount: result.discountPercent ?? 50, expiresAt: result.expiresAt })
      } else {
        setPromoError(result.error || 'Invalid code')
      }
    } finally {
      setPromoValidating(false)
    }
  }

  const handleCheckout = async () => {
    setLoading(true)
    trackCheckoutStarted({ plan: billing, trial: true })
    const origin = window.location.origin
    // Redeem a typed promo code to this account BEFORE checkout — the server
    // only attaches the Stripe coupon once the code is redeemed to the
    // account's email, so sending it straight to create-checkout silently
    // dropped the discount (this mirrors the extension's promo flow).
    const typedCode = promoCode.trim()
    if (typedCode && signedIn) {
      const redeem = await redeemPromoCode(typedCode.toUpperCase())
      if (!redeem.success) {
        // "Already redeemed a code" means this account already has one
        // linked — create-checkout falls back to the stored code, so don't
        // block the purchase on it.
        if (!/already redeemed/i.test(redeem.error || '')) {
          setLoading(false)
          setPromoError(redeem.error || 'Invalid code')
          return
        }
      }
    }
    let result: CreateCheckoutResult
    try {
      result = await createCheckout({
        plan: billing,
        tier: proPlusAvailable ? tier : undefined,
        // Stripe fills in {CHECKOUT_SESSION_ID}: it is the purchase event's
        // transaction id, so a reload of the return page is not a second sale.
        // This override of apps/api's default used to leave it out.
        successUrl: `${origin}/?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${origin}/?checkout=canceled`,
        promoCode: promoCode.trim() || undefined,
        trialDays: 30,
        locale: localeMeta(locale).stripeLocale,
      }).unwrap()
    } catch {
      setLoading(false)
      toast.error('Unable to start checkout')
      return
    }
    setLoading(false)
    if (result.provider === 'paddle' && result.transactionId) {
      // Paddle: the server made the transaction; open the overlay on it and return to the page
      // Stripe returns to. The transaction id stands in for Stripe's session id, which the return
      // handler only uses to join the purchase to its server-side record.
      removeSessionStorage(OAUTH_PAYWALL_KEY)
      writeSessionStorage('baba.checkout.plan', billing)
      writeSessionStorage('baba.checkout.trial', 'true')
      try {
        await openPaddleCheckout({
          transactionId: result.transactionId,
          email: result.email,
          successUrl: `${origin}/?checkout=success&session_id=${encodeURIComponent(result.transactionId)}`,
        })
      } catch {
        toast.error('Unable to start checkout')
      }
    } else if (result.url) {
      // The browser's InitiateCheckout carries the Stripe session id, the id of the server's copy.
      trackCheckoutSessionCreated({ plan: billing, sessionId: result.sessionId })
      removeSessionStorage(OAUTH_PAYWALL_KEY)
      writeSessionStorage('baba.checkout.plan', billing)
      writeSessionStorage('baba.checkout.trial', 'true')
      window.location.href = result.url
    } else if (result.authRequired) {
      setSignedIn(false)
      setMagicSent(false)
      toast.info('Session expired. Send a magic link and continue checkout.')
    } else if (result.alreadySubscribed) {
      let status = await dispatch(
        babaApi.endpoints.subscriptionStatus.initiate(undefined, { forceRefetch: true }),
      ).unwrap()
      if (!isSubscriptionActive(status)) {
        const restored = await restoreSubscription().unwrap()
        if (restored.restored || restored.already_active) {
          status = await dispatch(
            babaApi.endpoints.subscriptionStatus.initiate(undefined, { forceRefetch: true }),
          ).unwrap()
        }
      }
      if (isSubscriptionActive(status)) {
        broadcastSubscriptionStatus(status)
        toast.success('Your baba Pro plan is already active.')
        onClose()
        return
      }
      if (result.manageUrl) {
        window.location.href = result.manageUrl
        return
      }
      toast.error(result.error || 'You already have an active subscription.')
    } else {
      toast.error(result.error || 'Unable to start checkout')
    }
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !EMAIL_RE.test(email)) {
      toast.error('Please enter a valid email')
      return
    }
    setLoading(true)
    trackMagicLinkRequested({ intent: 'paywall' })
    const origin = window.location.origin
    let r: { ok: boolean; error?: string }
    try {
      r = await sendMagicLinkMutation({
        email,
        returnUrl: `${origin}/`,
        referralCode: getStoredReferralCode(),
      }).unwrap()
    } catch {
      r = { ok: false, error: 'Failed to send magic link' }
    }
    setLoading(false)
    if (r.ok) {
      setMagicSent(true)
      // The link is usually opened in another tab, and that sign-in reloads
      // this one: reopen on the same plan afterwards.
      writeSessionStorage(OAUTH_PAYWALL_KEY, JSON.stringify({ plan: billing }))
      toast.success('Check your inbox')
    } else {
      toast.error(r.error || 'Failed to send magic link')
    }
  }

  // The plan table shows Pro+ prices when Pro+ is selected and configured, Pro
  // prices otherwise — falls back to Pro whenever pricing.proPlus is missing so
  // the table never goes blank mid-toggle.
  const activeTierPricing = tier === 'pro_plus' && pricing?.proPlus ? pricing.proPlus : pricing

  // The API's display strings carry a "/mo" / "/yr" suffix; the i18n billing
  // templates add their own period label, so strip it here to avoid "$12.99/mo/mo".
  const priceMonthly = (activeTierPricing?.monthly?.display || '$12.99').replace(/\/(mo|yr)$/i, '')
  const priceYearly = (activeTierPricing?.yearly?.display || '$59.99').replace(/\/(mo|yr)$/i, '')
  // Full display (with the "/yr" suffix intact) for the plan-table price cell.
  const yearlyDisplay = activeTierPricing?.yearly?.display || '$59.99/yr'
  const yearlyPerMonth = useMemo(() => {
    const yearly = activeTierPricing?.yearly?.price ?? 59.99
    return (yearly / 12).toFixed(2)
  }, [activeTierPricing])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-md max-md:items-end max-md:p-0"
          style={{ background: 'rgba(0,0,0,0.55)' }}
          onClick={onClose}
        >
         <FocusLock returnFocus autoFocus>
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 16 }}
            transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            // Phone: a bottom sheet (top corners only, full width, safe-area
            // padding) instead of a centered desktop dialog.
            className="relative w-full max-w-[440px] md:max-w-[880px] rounded-md border border-app bg-surface overflow-hidden shadow-token-lg dialog-max-h flex flex-col max-md:max-w-none max-md:rounded-b-none max-md:rounded-t-[14px] max-md:border-x-0 max-md:border-b-0 max-md:pb-[env(safe-area-inset-bottom)]"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={reasonCopy.title}
            dir={layoutDir}
            lang={locale}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t.common.close}
              className="touch-44 absolute top-2.5 right-2.5 md:top-4 md:right-4 h-7 w-7 rounded-md flex items-center justify-center text-subtle hover:text-app hover:bg-[var(--hover)] transition-colors duration-150 z-10 cursor-pointer"
              dir="ltr"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>

            {/* Language picker — pinned top-left opposite the close button */}
            <div className="absolute top-3 left-3 z-10" dir="ltr">
              <LocalePicker label={t.paywall.picker.change} />
            </div>

            {/* min-h-0 or this scroller cannot shrink inside the flex column,
              * and the dialog's own bottom gets cut off on a short phone. */}
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
              <div className="px-7 pt-7 pb-7 flex flex-col md:grid md:grid-cols-[1fr_336px] md:grid-rows-[auto_1fr] md:gap-x-8 md:items-start">
                <div className="order-1 md:order-none md:pt-1 md:col-start-1 md:row-start-1">
                <div className="flex flex-col items-center text-center mb-5">
                  <div className="flex items-center gap-2 mb-4">
                    <img
                      src="/baba-app-icon.png"
                      alt=""
                      className="h-8 w-8 object-cover"
                      style={{ borderRadius: '8px' }}
                    />
                    <span className="text-[16px] font-semibold text-app">baba</span>
                  </div>
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wider border mb-3"
                    style={{ background: 'var(--card-2)', color: 'var(--muted)', borderColor: 'var(--line)', borderRadius: '4px' }}
                  >
                    {reasonCopy.badge}
                  </span>
                  <h2 className="text-[20px] font-semibold tracking-tight leading-tight text-app mb-1.5">
                    {reasonCopy.title}
                  </h2>
                  <p className="text-[13px] text-muted leading-relaxed">
                    {reasonCopy.subtitle}
                  </p>
                </div>
                </div>
                <div className="order-3 md:order-none md:col-start-1 md:row-start-2">
                {showUsageBanner && usage?.charsLimit ? (
                  <UsageBanner usage={usage} />
                ) : null}
                {proPlusAvailable && <PaywallProPlusComparison copy={t.paywall.proPlusComparison} />}
                {proPlusAvailable && tier === 'pro_plus' ? (
                  <PaywallProPlusBenefits copy={t.paywall.proPlusBenefits} />
                ) : reasonFeatureList ? (
                  <ul className="space-y-2 mb-5">
                    {reasonFeatureList.map((feature) => (
                      <li key={feature} className="flex items-start gap-2 text-[13px] text-app leading-snug">
                        <span className="shrink-0 mt-0.5" style={{ color: 'var(--muted-2)' }}>
                          <Check className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="grid grid-cols-2 gap-x-3 gap-y-2.5 mb-5">
                    {FEATURE_ICONS.map(({ key, icon: FIcon }) => (
                      <li key={key} className="flex items-start gap-2 text-[13px] text-app leading-snug">
                        <span className="shrink-0 mt-0.5" style={{ color: 'var(--muted-2)' }}>
                          <FIcon className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <span>{t.paywall.features[key]}</span>
                      </li>
                    ))}
                  </ul>
                )}
                </div>

                <div className="order-2 md:order-none mt-6 md:mt-0 md:pl-8 md:border-l md:border-app md:col-start-2 md:row-start-1 md:row-span-2">
                {proPlusAvailable && (
                  <PaywallTierToggle tier={tier} onChange={setTier} labels={t.paywall.tierToggle} />
                )}
                {/* Plan table — two rows, price + per-month equivalent columns. */}
                <div className="mb-3 rounded-md border border-app overflow-hidden" role="radiogroup" aria-label={`${t.paywall.billing.yearly} / ${t.paywall.billing.monthly}`}>
                  <table className="w-full border-collapse text-[13px]">
                    <tbody>
                      <tr
                        role="radio"
                        aria-checked={billing === 'yearly'}
                        tabIndex={0}
                        onClick={() => setBilling('yearly')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setBilling('yearly')
                          }
                        }}
                        className="cursor-pointer"
                        style={{ background: billing === 'yearly' ? 'var(--accent-soft)' : 'transparent' }}
                      >
                        <td className="py-2.5 px-3 font-medium text-app">
                          <span className="inline-flex items-center gap-2">
                            <PlanRadio selected={billing === 'yearly'} />
                            {t.paywall.billing.yearly}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-app text-right" dir="ltr">{yearlyDisplay}</td>
                        <td className="py-2.5 px-3 font-mono text-subtle text-right text-[11px]" dir="ltr">
                          {t.paywall.billing.pricedMonthly(`$${yearlyPerMonth}`)}
                        </td>
                      </tr>
                      <tr
                        role="radio"
                        aria-checked={billing === 'monthly'}
                        tabIndex={0}
                        onClick={() => setBilling('monthly')}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setBilling('monthly')
                          }
                        }}
                        className="cursor-pointer border-t border-app"
                        style={{ background: billing === 'monthly' ? 'var(--accent-soft)' : 'transparent' }}
                      >
                        <td className="py-2.5 px-3 font-medium text-app">
                          <span className="inline-flex items-center gap-2">
                            <PlanRadio selected={billing === 'monthly'} />
                            {t.paywall.billing.monthly}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 font-mono text-app text-right" dir="ltr">
                          {t.paywall.billing.pricedMonthly(priceMonthly)}
                        </td>
                        <td className="py-2.5 px-3 font-mono text-subtle text-right" dir="ltr"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-2 mb-5 text-[13px] text-muted">
                  <DevicesIcon className="h-4 w-4 shrink-0" />
                  <span>{t.paywall.crossPlatform.note}</span>
                </div>
                {signedIn && (
                  <div className="mb-3 text-center text-[12px] text-brand font-semibold">
                    {tier === 'pro_plus' ? t.paywall.cta.noTrialHint : t.paywall.cta.trialHint}
                  </div>
                )}
                {signedIn ? (
                  <>
                    <button
                      type="button"
                      onClick={handleCheckout}
                      disabled={loading}
                      className="w-full h-11 rounded-md font-semibold text-[14px] transition-all duration-150 hover:brightness-105 active:scale-[0.98] shadow-md shadow-brand-500/25 disabled:opacity-50 cursor-pointer select-none"
                      style={{
                        background: 'var(--accent)',
                        border: '1px solid var(--accent-deep)',
                        color: 'var(--cta-fg)',
                      }}
                    >
                      {loading ? t.paywall.cta.loading : tier === 'pro_plus' ? t.paywall.cta.startProPlus : t.paywall.cta.startTrial}
                    </button>
                    {/* Upfront about the card ask — Stripe collects it at
                        checkout, so say so here instead of surprising users. */}
                    <p className="mt-2 text-center text-[12px] text-muted" dir="ltr">
                      {tier === 'pro_plus' ? 'Billed today · no trial on Pro+ · cancel anytime' : '30 days free · card required · cancel anytime'}
                    </p>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        // Stash the checkout context before the OAuth
                        // round-trip so PaywallProvider can reopen the
                        // paywall (same plan) when we land back signed-in.
                        writeSessionStorage(OAUTH_PAYWALL_KEY, JSON.stringify({ plan: billing }))
                        startGoogleSignIn()
                      }}
                      disabled={loading}
                      className="w-full h-10 rounded-md font-semibold text-app text-[13px] flex items-center justify-center gap-2.5 bg-surface border border-app hover:bg-[var(--card-2)] hover:border-[var(--border-strong)] shadow-xs active:scale-[0.98] transition-all duration-150 disabled:opacity-50 mb-3 cursor-pointer select-none"
                    >
                      <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                        <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                        <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"/>
                        <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"/>
                      </svg>
                      Continue with Google
                    </button>

                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 h-px bg-app/20" />
                      <span className="text-[11px] text-subtle uppercase tracking-wider">or</span>
                      <div className="flex-1 h-px bg-app/20" />
                    </div>

                    <form onSubmit={handleMagicLink} className="space-y-2.5">
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t.paywall.cta.emailPlaceholder}
                        className="w-full h-10 px-3.5 rounded-md bg-surface border border-app text-app placeholder:text-subtle focus:outline-none focus:border-[var(--accent)] focus:ring-[3px] focus:ring-[var(--accent-soft)] transition-colors text-[13px]"
                      />
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-11 rounded-md font-semibold text-[14px] transition-all duration-150 hover:brightness-105 active:scale-[0.98] shadow-md shadow-brand-500/25 disabled:opacity-50 cursor-pointer select-none"
                        style={{
                          background: 'var(--accent)',
                          border: '1px solid var(--accent-deep)',
                          color: 'var(--cta-fg)',
                        }}
                      >
                        {loading ? t.paywall.cta.sending : magicSent ? t.paywall.cta.checkInbox : t.paywall.cta.continueEmail}
                      </button>
                      <p className="text-[12px] text-muted text-center leading-relaxed">
                        {t.paywall.cta.magicLinkHint}
                      </p>
                    </form>
                  </>
                )}
                <div className="mt-4 flex items-center justify-center gap-3 text-[12px] text-muted">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="h-3 w-3" strokeWidth={2} />
                    {t.paywall.footer.stripeSecured}
                  </span>
                  <span className="opacity-30">·</span>
                  <span>{t.paywall.footer.cancelAnytime}</span>
                  <span className="opacity-30">·</span>
                  <span>{t.paywall.footer.noHiddenFees}</span>
                </div>
                <div className="mt-4 text-center">
                  {showPromo ? (
                    <div className="space-y-2">
                      <div className="relative">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(e) => {
                            setPromoCode(e.target.value.toUpperCase())
                            setPromoValidated(null)
                            setPromoError(null)
                          }}
                          onBlur={() => {
                            const code = promoCode.trim()
                            if (PROMO_CODE_RE.test(code) && !promoValidated && !promoValidating) {
                              void handlePromoValidate(code)
                            }
                          }}
                          placeholder={t.paywall.cta.promoPlaceholder}
                          autoComplete="off"
                          spellCheck={false}
                          className={cn(
                            'w-full h-9 px-3.5 rounded-md bg-surface border text-app placeholder:text-subtle focus:outline-none transition-colors text-[12px] text-center tracking-wider font-semibold',
                            promoValidated
                              ? 'border-[var(--ok)] focus:border-[var(--ok)]'
                              : promoError
                                ? 'border-[var(--err)] focus:border-[var(--err)]'
                                : 'border-app focus:border-[var(--accent)]',
                          )}
                        />
                        {promoValidating && (
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full border-2 border-subtle border-t-transparent animate-spin" aria-hidden />
                        )}
                        {promoValidated && !promoValidating && (
                          <svg viewBox="0 0 24 24" className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--ok)]" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      {promoValidated && !promoError && (
                        <p className="text-[11px] font-medium text-[var(--ok)]" role="status">
                          {promoValidated.discount}% off for the life of your subscription, applied at checkout
                        </p>
                      )}
                      {promoError && (
                        <p className="text-[11px] font-medium text-[var(--err)]" role="alert">
                          {promoError}
                        </p>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowPromo(true)}
                      className="touch-44-y text-[12px] text-muted hover:text-app underline underline-offset-2 transition-colors"
                    >
                      {t.paywall.cta.promoPrompt}
                    </button>
                  )}
                </div>
                <p className="mt-4 text-center text-[12px] text-muted leading-relaxed">
                  {t.paywall.footer.oneSubscription}
                </p>
                {/* A mobile subscriber has no email on file here (Apple never
                    exposes the App Store email), so nothing in this flow can
                    auto-detect them — they'd look Free and could buy again.
                    Copy is the only available guard. Path matches the mobile
                    app's actual Settings IA — see mobile's SignInWebSheet /
                    AccountSyncPage — and links here so a signed-in web user can
                    generate a link code without hunting for the settings page. */}
                <p className="mt-3 text-center text-[10.5px] text-subtle leading-relaxed opacity-60">
                  Already subscribed in the baba app? Don&rsquo;t pay twice. Open baba on your phone, go to
                  Settings → Sign in to baba, and use{' '}
                  {EMAIL_RE.test(email) ? `this email (${email})` : 'the same email you use here'} (or Google, or Apple on iPhone). Or
                  tap &ldquo;Have a link code instead?&rdquo; and enter a code from{' '}
                  <Link to="/settings/account-sync" onClick={onClose} className="underline underline-offset-2 hover:text-app transition-colors">
                    Settings › Phone & Sync
                  </Link>
                  . Your Pro appears automatically.
                </p>
                </div>
              </div>
            </div>
          </motion.div>
         </FocusLock>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

function UsageBanner({ usage }: { usage: UsageSnapshot }) {
  const used = usage.charsUsed ?? 0
  const limit = usage.charsLimit ?? 0
  if (limit <= 0) return null
  const pct = Math.min(100, (used / limit) * 100)
  const period = usage.period === 'yearly' ? 'this year' : 'this month'
  return (
    <div className="mb-5 p-3.5 rounded-md bg-[var(--warning-bg)] border border-[var(--warning)]/25">
      <div className="flex items-center justify-between text-[11px] font-semibold mb-1.5">
        <span className="text-[var(--warning)]">Characters used {period}</span>
        <span className="text-[var(--warning)] tabular-nums">
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: 'var(--warning)' }}
        />
      </div>
    </div>
  )
}
